import { useEffect, useSyncExternalStore } from 'react';

import { usePendingPicksCount } from '@/renderer/utils/hooks/usePendingPicksCount';

/**
 * The last pending count the sidebar badge rendered, for the mobile tab bar's
 * Results badge (#2142). The sidebar badge stays the single mount of
 * usePendingPicksCount for navigation (the sidebar is CSS-hidden on phones but
 * still mounted), and the tab bar observes it here, the same way its Chat badge
 * observes the sidebar's unread counter through renderer/utils/appBadge.ts.
 * A third hook mount would add another WS subscription for the same number.
 */
let publishedCount = 0;
const listeners = new Set<() => void>();

function publish(count: number) {
  if (publishedCount !== count) {
    publishedCount = count;
    for (const listener of listeners) {
      listener();
    }
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The member's pending-picks count, as the sidebar badge last showed it. */
export function usePublishedPendingCount(): number {
  return useSyncExternalStore(
    subscribe,
    () => publishedCount,
    () => 0,
  );
}

/**
 * Small "N pending" badge for the My Results nav item — the count of the
 * member's ungraded, non-future plays across all sports. Renders nothing when
 * there are none (or when logged out).
 *
 * The count is ghost-aware (see usePendingPicksCount): a fresh or returning
 * subscriber with no messageReceipts still sees a correct "N pending", and it
 * always matches the My Results pending section (same source, same filters).
 * Live-updates on new releases/grades are preserved by the shared hook (#1459).
 */
export function PendingCountBadge() {
  const { count, loggedIn } = usePendingPicksCount();
  const visible = loggedIn ? count || 0 : 0;

  useEffect(() => {
    publish(visible);
  }, [visible]);

  useEffect(() => () => publish(0), []);

  if (!visible) {
    return null;
  }

  return (
    <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-amber-400/90 px-3 py-2 text-xs font-medium leading-none text-zinc-900">
      {visible}
    </span>
  );
}
